import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getAllSections, saveSection } from '../firebase/content';

/* ─────────────────────────────────────────────────────────
   DEFAULT CONTENT  — mirrors current hardcoded site content.
   Used as fallback when Firestore has no data yet.
───────────────────────────────────────────────────────── */
export const DEFAULT_CONTENT = {
  global: {
    siteName: 'Klaivox',
    tagline: 'Intelligent Conversations. Everywhere.',
    primaryColor: '#4F8EF7',
    secondaryColor: '#6366f1',
    backgroundColor: '#060d1a',
    metaTitle: 'Klaivox — White-Label AI Agents',
    metaDescription: 'Deploy fully branded AI agents under your name.',
    bookingUrl: 'https://calendar.app.google/6wNXN6as4MAe7NpMA',
    analyticsId: '',
  },
  navigation: {
    links: [
      { label: 'Home', url: '/', newTab: false },
      { label: 'Platform', url: '/features', newTab: false },
      { label: 'Pricing', url: '/pricing', newTab: false },
      { label: 'Onboarding', url: '/onboarding', newTab: false },
      { label: 'Contact', url: '/contact', newTab: false },
    ],
    ctaLabel: 'Book a Demo',
    ctaUrl: '/contact',
    testLabel: 'Test It Now',
    testUrl: 'https://app.klaivox.com/preview?uid=dcf9fcb8-c689-4c95-a23f-9d009bca2ea0',
  },
  hero: {
    label: 'AI-Powered Customer Service for Your Business',
    headline1: 'Your Business, Answering Every Customer.',
    headline2: '24 Hours a Day.',
    subtext: 'A branded AI assistant that answers calls, books appointments, and handles inquiries — automatically, around the clock.',
    btn1Label: 'Book My Free 30-Min Demo →',
    btn1Url: '/contact',
    btn2Label: 'See How It Works',
    btn2Url: '/features',
    trustText: 'No long-term contracts · Your brand, not ours · Live in under 24 hours',
  },
  benefits: {
    heading: 'Stop Losing Leads After Hours — Let AI Handle It for You',
    items: [
      { title: 'Your Brand, Your AI', desc: "Your customers see your business name, logo, and domain on every interaction. The AI feels like it's part of your team — because to them, it is." },
      { title: 'Never Miss a Lead Again', desc: 'Your AI answers every call, replies to every message, and books every appointment — even at 2am on a Sunday.' },
      { title: 'Appointments Booked Automatically', desc: 'Your AI checks your calendar, offers available slots, confirms the booking, and sends reminders — all without any staff involvement.' },
      { title: 'Live in Under 24 Hours', desc: 'No technical skills needed. We configure everything on a single setup call.' },
      { title: '160+ Languages Supported', desc: 'Your AI speaks to customers in their language — English, French, Spanish, Mandarin, and 160+ more.' },
      { title: 'One Flat Monthly Fee', desc: 'No per-conversation fees, no per-seat charges, no surprise invoices. From $29 CAD/month — all channels, all integrations, all support.' },
    ],
  },
  howItWorks: {
    heading: 'From Zero to Live AI Agent in Under 24 Hours',
    subtext: 'Simple steps to get your AI assistant up and running.',
    steps: [
      { num: '01', title: 'Book Your Setup Call', desc: 'Schedule a free onboarding session with our team.' },
      { num: '02', title: 'Connect Your Brand', desc: 'We apply your logo, colors, and custom domain.' },
      { num: '03', title: 'Train Your AI Agent', desc: 'Configure knowledge, tone, and automated workflows.' },
      { num: '04', title: 'Go Live & Start Earning', desc: 'Launch your branded AI and start invoicing clients immediately.' },
    ],
  },
  features: {
    heading: 'Everything Your Clients Need to Run on Autopilot',
    subtext: 'Voice, SMS, email, and web chat — your AI handles all of it, 24/7, under your brand.',
    items: [
      { title: 'Fully Branded as Your Business', desc: 'Every screen, every message carries your name and logo.', list: ['Custom logo, colors & domain', 'Branded client dashboards', 'AI agent named as your business', 'Analytics under your brand'] },
      { title: 'AI-Powered by GPT-4o, Claude & Grok', desc: 'Choose the right model per agent for best quality, speed, and cost.', list: ['OpenAI GPT-4o & GPT-4o-mini', 'Anthropic Claude 3.7 Sonnet', 'xAI Grok-2', '160+ languages supported'] },
      { title: 'Every Channel Your Customers Use', desc: 'Voice, chat, WhatsApp, Facebook, Instagram — all in one platform.', list: ['Inbound & outbound voice calls', 'Website chat widget', 'WhatsApp Business API', 'Facebook Messenger & Instagram DMs'] },
      { title: 'Knowledge Base & Smart Training', desc: 'Train on your business in minutes — auto-updates daily.', list: ['Auto-scrapes your website', 'Upload PDFs and documents', 'Custom AI instructions', 'Source-cited answers'] },
      { title: 'Integrations & Workflow Automation', desc: '26 native integrations including Google Calendar, Stripe, WhatsApp, SendGrid, Salesforce, HubSpot, Shopify, and more.', list: ['Google Calendar', 'WhatsApp Business API', 'Stripe payments', 'Salesforce & HubSpot CRM', 'SendGrid & Gmail', 'Shopify & more'] },
      { title: 'Analytics & Reporting', desc: 'See every conversation, lead, and booking in your dashboard.', list: ['Conversation volume & resolution', 'Leads captured & bookings', 'Channel performance', 'Export-ready reports'] },
    ],
  },
  industries: {
    heading: 'See How It Works in Your Industry',
    items: [
      { name: 'Real Estate', bullets: ['A realtor stops losing leads after hours', 'Buyers get instant property info', 'Every prospect gets a follow-up automatically'] },
      { name: 'Medical', bullets: ['A clinic fills its calendar automatically', 'Staff stop fielding routine calls', 'Patients complete intake forms before visits'] },
      { name: 'Legal', bullets: ['A law firm converts more visitors into consultations', 'Prospects get answers immediately', 'No lead goes cold'] },
      { name: 'E-Commerce', bullets: ['Customers get order updates instantly', 'Product questions answered automatically', 'Abandoned carts recovered with follow-ups'] },
      { name: 'Services', bullets: ['Never miss an inbound call again', 'Quotes and confirmations go out automatically', 'AI follows up for reviews and rebooking'] },
    ],
  },
  pricing: {
    heading: 'Hire an AI Employee for Less Than One Shift a Month',
    subtext: 'Never miss a lead again. Klaivox answers calls, automates emails, follows up with customers, and helps your business run 24/7 — without adding payroll.',
    tiers: [
      {
        name: 'Basic', amount: '$29 CAD', period: '/month', featured: false,
        desc: 'Everything you need to stop missing website visitors.',
        features: ['AI website chat agent, branded as your business', '24/7 automated website chat', 'Lead capture and contact collection', 'Automated email follow-ups', 'Knowledge base trained on your website', 'Cancel anytime'],
        footnote: 'No setup fee · Cancel anytime',
        ctaLabel: 'Get Started', ctaUrl: '/pricing',
      },
      {
        name: 'Growth', amount: '$99 CAD', period: '/month', featured: true, badge: 'Recommended',
        desc: 'Capture every lead — on your website and on the phone.',
        features: ['Everything in Basic', 'AI chat + AI phone agent', 'Answers your calls 24/7', 'Appointment booking via chat & phone', '100 voice minutes included', 'Lead qualification and routing', 'API & webhook integrations', 'Priority support'],
        footnote: 'No setup fee · Cancel anytime',
        ctaLabel: 'Get Started', ctaUrl: '/pricing',
      },
      {
        name: 'Advanced', amount: 'Custom', period: '', featured: false,
        desc: 'The full AI stack for growing businesses.',
        features: ['Everything in Growth', 'Multiple AI chat agents', 'Multiple AI voice phone numbers', 'Custom integrations & webhooks', 'Custom AI training (website + documents)', 'Higher usage limits', 'Multi-language support', 'Conversation history & analytics', 'Dedicated onboarding'],
        footnote: 'Custom scope · No setup fee · No long-term contract',
        ctaLabel: 'Contact Us', ctaUrl: '/contact',
      },
    ],
  },
  faq: {
    heading: 'Common Questions',
    subtext: '',
    items: [
      { q: 'Is there a contract?', a: 'No. Cancel anytime — no lock-in, no cancellation fees.' },
      { q: 'Is setup included?', a: 'Yes. White-glove setup is included — founding customers pay no setup fee.' },
      { q: 'How long does setup take?', a: 'Most businesses can launch within days depending on complexity.' },
      { q: 'Can it work with my current tools?', a: 'Yes. Klaivox supports common CRM and communication integrations.' },
    ],
  },
  contact: {
    heading: 'Book a Free Demo',
    subtext: "See your AI agent in action — live, in 30 minutes. We'll show you exactly how it works for your specific business.",
    email: 'klaivoxai@gmail.com',
    phone: '226-220-8759',
    website: 'klaivox.com',
    hours: 'Monday – Friday, 9:00 AM – 6:00 PM EST',
    bookingUrl: 'https://calendar.app.google/6wNXN6as4MAe7NpMA',
    expectItems: [
      'Live demo of your branded AI agent',
      'See it answer calls and handle bookings in real time',
      'Get a custom setup plan for your business',
      'Pricing and next steps — no pressure',
    ],
  },
  footer: {
    tagline: 'Intelligent Conversations. Everywhere.\nDeploy fully branded AI agents under your name.',
    copyright: 'Klaivox. All rights reserved.',
    contactEmail: 'klaivoxai@gmail.com',
    contactPhone: '226-220-8759',
    contactWebsite: 'klaivox.com',
    hours: 'Monday – Friday, 9:00 AM – 6:00 PM EST',
    instagramUrl: 'https://www.instagram.com/klaivox_',
  },
};

/* ─────────────────────────────────────────────────────────
   CONTEXT
───────────────────────────────────────────────────────── */
export const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    getAllSections()
      .then(remote => {
        if (remote && Object.keys(remote).length > 0) {
          setContent(prev => {
            const merged = { ...prev };
            Object.keys(remote).forEach(k => {
              if (remote[k]) merged[k] = { ...prev[k], ...remote[k] };
            });
            return merged;
          });
        }
      })
      .catch(() => {}) // silently fall back to defaults
      .finally(() => setLoading(false));
  }, []);

  const updateSection = useCallback(async (id, data) => {
    await saveSection(id, data);
    setContent(prev => ({ ...prev, [id]: data }));
  }, []);

  return (
    <ContentContext.Provider value={{ content, loading, updateSection }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContentContext() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContentContext must be inside ContentProvider');
  return ctx;
}
